import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { KNOWLEDGE_TYPES } from 'shared/constants/knowledge.constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { TextInput, Modal } from '../design-system';
import { isAppUrl } from 'shared/utils';
const KnowledgeUploadExternalLinkComponent = React.memo(
  function KnowledgeUploadExternalLinkComponent({
    open = false,
    close = () => {},
    editExternal = () => {},
    editContent = () => {},
    ...props
  }) {
    const lang = useSelector(getLang('KNOWLEDGE_UPLOAD_POPUP'));
    const langEditor = useSelector(getLang('KNOWLEDGE_EDITOR'));

    const EXTERNAL_LINK_SCHEME = useMemo(
      () =>
        Yup.object().shape({
          link: Yup.string()
            .url()
            .test(
              'not-app-url',
              langEditor.APP_URL_IN_EXTERNAL_LINK_ERROR,
              value => !isAppUrl(value) && !/\/knowledge\/items\/[a-fA-F0-9]+$/.test(value),
            )
            .required(),
        }),
      [langEditor],
    );

    const onSubmit = useCallback(
      (values, { resetForm }) => {
        editExternal({
          labels: props.labels,
          ownerIds: props.ownerIds,
          subtitle: props.subtitle,
          tags: props.tags,
          link: values.link,
          type: KNOWLEDGE_TYPES.EXTERNAL_LINK,
        });
        resetForm();
        close();
      },
      [close, props, editExternal],
    );

    const { handleSubmit, setFieldValue, values, errors, isValid, dirty } = useFormik({
      initialValues: {
        link: '',
      },
      validationSchema: EXTERNAL_LINK_SCHEME,
      onSubmit: onSubmit,
    });

    const handleLinkChange = useCallback(
      e => {
        const value = e.target.value;
        // Manually replace spaces with %20
        const transformedValue = value.trim().replace(/\s/g, '%20');
        setFieldValue('link', transformedValue);
      },
      [setFieldValue],
    );

    return (
      <Modal
        title={lang.TITLE}
        open={open}
        onCancel={close}
        onSubmit={handleSubmit}
        cancelText={lang.DISMISS}
        submitText={lang.SUBMIT}
        submitDisabled={!isValid || !dirty}>
        <form onSubmit={handleSubmit}>
          <TextInput
            name="link"
            value={values.link}
            error={errors.link}
            placeholder={langEditor.LINK_PLACEHOLDER}
            label={langEditor.LINK}
            onChange={handleLinkChange}
            size="small"
            required
          />
        </form>
      </Modal>
    );
  },
);

export { KnowledgeUploadExternalLinkComponent };
